import { requestLogout } from 'components/LogoutConfirm/LogoutConfirm';
import { ClipboardList, FilePen, MessagesSquare, Settings, UserRound } from 'lucide-react';
import type React from 'react';
import { useMemo } from 'react';

import { RoleSwitcher } from 'components/Header/RoleSwitcher';
import { VerificationStatusBadge } from 'components/VerificationStatusBadge';
import { getPhotoHeaderStatus, useFetchPhotos } from 'hooks/usePhotos';
import { useProfileModeration } from 'hooks/useProfileModeration';
import { useVerificationEdits } from 'hooks/useVerificationEdits';
import profileIcon from '../../../assets/icons/Header/Group 27.svg';
import { getRole } from 'api/tokenStorage';
import { ChatReportMenuButton } from 'modules/ChatV2/components/ChatReportMenuButton';
import { MenuItem } from 'ui/kit/Kit';
import kit from 'ui/kit/kit.module.scss';
import { useGlobalPresence } from 'modules/ChatV2/store/GlobalPresenceContext';
import { useGetCurrentProfile } from '../model';
import classes from '../Profile.module.scss';

export type ProfileMenuProps = {
  activeSection: string;
  onChange: (section: string) => void;
};

export const ProfileMenu: React.FC<ProfileMenuProps> = ({ activeSection, onChange }) => {
  const { data } = useGetCurrentProfile();
  const { isUserOnline } = useGlobalPresence();
  const roleLabel = getRole() === 'tutor' ? 'Репетитор' : 'Ученик';
  const { isApproved } = useProfileModeration();
  const { hasRejectedItems } = useVerificationEdits();
  const currentUserId = data?.id_user ?? data?.id;
  const isOnline = isUserOnline(currentUserId);

  const photoToken = useMemo(
    () => (data?.profile_photo_token ? [data.profile_photo_token] : []),
    [data?.profile_photo_token],
  );
  const { data: currentPhoto } = useFetchPhotos(photoToken);

  const photoPath = useMemo(() => {
    if (
      getPhotoHeaderStatus(currentPhoto?.[0]?.headers) === 'approved' &&
      currentPhoto?.[0]?.blobUrl
    ) {
      return currentPhoto[0].blobUrl;
    }
    return profileIcon;
  }, [currentPhoto]);

  return (
    <aside
      className={classes['profile-menu']}
      style={{ ['--menu-on' as string]: '#f3d1b0' }}
    >
      <div className={classes['profile-menu__header']}>
        <div className={classes['profile-menu__header-content']}>
          <div className={classes['profile-menu__avatar']}>
            <img src={photoPath} alt='' />
            {isOnline && <i />}
          </div>
          <div className={classes['profile-menu__info']}>
            <span className={classes['profile-menu__name']}>{data?.name}</span>
            <span className={classes['profile-menu__role']}>{roleLabel}</span>
          </div>
        </div>

        {/* Проверка профиля временно отключена
        {data?.profile_verified ? (
          <span className={classes['profile-menu__status']}>
            <span className={classes['profile-menu__status-icon']}>
              <AcceptIcon />
            </span>
            <span style={{ paddingTop: '3px' }}>Профиль подтвержден</span>
          </span>
        ) : (
          <span className={classes['profile-menu__status']}>
            <span className={classes['profile-menu__status-icon']}>
              <AcceptIconTeacher />
            </span>
            <span style={{ paddingTop: '3px' }}>Профиль не подтвержден</span>
          </span>
        )}
        */}
        <VerificationStatusBadge
          role='student'
          status={data?.verification_status}
          comment={data?.verification_comment}
        />
      </div>

      <div className={classes['profile-menu__role-switcher']}>
        <RoleSwitcher variant='menu' />
      </div>

      <nav className={classes['profile-menu__nav']} aria-label='Разделы кабинета'>
        <MenuItem label='Мои задачи' icon={<ClipboardList />} active={activeSection === 'tasks'} onClick={() => onChange('tasks')} />
        <MenuItem label='Мой профиль' icon={<UserRound />} active={activeSection === 'profile'} onClick={() => onChange('profile')} />
        {hasRejectedItems ? (
          <MenuItem label='Редактирования' icon={<FilePen />} active={activeSection === 'edits'} onClick={() => onChange('edits')} />
        ) : null}
        <MenuItem
          label='Мои чаты'
          icon={<MessagesSquare />}
          active={activeSection === 'chats'}
          locked={!isApproved}
          hint='Доступно после одобрения профиля'
          onClick={() => onChange('chats')}
        />
        <MenuItem label='Настройки' icon={<Settings />} active={activeSection === 'settings'} onClick={() => onChange('settings')} />
      </nav>
      <hr className={kit.menuDivider} />
      <nav className={kit.menuSecondaryGroup} aria-label='Помощь и выход'>
        <MenuItem label='Частые вопросы' secondary active={activeSection === 'questions'} onClick={() => onChange('questions')} />
        <MenuItem label='Тех.поддержка' secondary active={activeSection === 'help'} onClick={() => onChange('help')} />
        <ChatReportMenuButton className={`${kit.menu} ${kit.menuSecondary}`} />
        <MenuItem label='Выход' secondary onClick={() => requestLogout()} />
      </nav>
    </aside>
  );
};
